import { useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { Package, Truck, Clock, ShieldCheck, MapPin, CalendarIcon, PackageCheck, Boxes } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TIME_SLOTS = ["9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM"];
const EMPTY = { name: "", phone: "", email: "", package_count: "1", notes: "" };

const STEPS = [
  { icon: Package, title: "Pack it up", desc: "Box and seal your item, or bring it in and we'll help you pack it right." },
  { icon: CalendarIcon, title: "Book a drop-off", desc: "Reserve a quick appointment below so we're ready when you arrive." },
  { icon: Truck, title: "We ship it UPS", desc: "As an official UPS shipping partner, we hand your package to UPS for delivery." },
];

export default function Shipping() {
  const [form, setForm] = useState(EMPTY);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (!date) return toast.error("Please choose a drop-off date.");
    if (!time) return toast.error("Please choose a time slot.");
    setLoading(true);
    try {
      await api.post("/shipping/appointments", {
        name: form.name, phone: form.phone, email: form.email,
        package_count: Number(form.package_count), preferred_date: format(date, "yyyy-MM-dd"),
        preferred_time: time, notes: form.notes || null,
      });
      toast.success("Drop-off appointment reserved! See you soon.");
      setForm(EMPTY); setDate(null); setTime("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to reserve.");
    } finally { setLoading(false); }
  }

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden grain-bg">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-20">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#351C15] px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#FFB500]">
              <Truck className="h-4 w-4" /> Official UPS Shipping Partner
            </span>
            <h1 className="mt-5 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Let's Ship!
            </h1>
            <p className="mt-6 text-base text-muted-foreground">
              Skip the trip across town. Crystal Pharmacy is your neighborhood UPS drop-off point in Raton — bring your
              prepaid or ready-to-go packages to us and we'll get them on their way with UPS. Reserve a quick drop-off
              appointment below so we're ready the moment you walk in.
            </p>
            <div className="mt-6 flex flex-wrap gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> Secure handling</span>
              <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> Fast, friendly drop-off</span>
              <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> 174 Hospital Dr, Raton</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-7xl px-6 pb-4">
        <div className="grid gap-5 sm:grid-cols-3">
          {STEPS.map((s, i) => (
            <div key={s.title} className="rounded-2xl border border-border bg-card p-6" data-testid={`ship-step-${i}`}>
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-primary"><s.icon className="h-6 w-6" /></div>
              <div className="flex items-center gap-2">
                <span className="font-heading text-sm font-bold text-primary">Step {i + 1}</span>
              </div>
              <h3 className="mt-1 font-heading text-lg font-bold">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Reservation form */}
      <section className="mx-auto max-w-3xl px-6 py-14">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-primary"><PackageCheck className="h-6 w-6" /></div>
          <div>
            <h2 className="font-heading text-2xl font-bold tracking-tight">Reserve a drop-off appointment</h2>
            <p className="text-sm text-muted-foreground">Tell us when you're coming and how many packages you're dropping off.</p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-5 rounded-2xl border border-border bg-card p-6 sm:p-8">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2"><Label>Full name</Label><Input data-testid="ship-name" value={form.name} onChange={set("name")} required /></div>
            <div className="space-y-2"><Label>Phone number</Label><Input data-testid="ship-phone" value={form.phone} onChange={set("phone")} required /></div>
          </div>
          <div className="space-y-2"><Label>Email</Label><Input type="email" data-testid="ship-email" value={form.email} onChange={set("email")} required /></div>
          <div className="grid gap-5 sm:grid-cols-3">
            <div className="space-y-2">
              <Label>Packages</Label>
              <div className="relative">
                <Boxes className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input type="number" min="1" className="pl-9" data-testid="ship-count" value={form.package_count} onChange={set("package_count")} required />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Drop-off date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" data-testid="ship-date" className="w-full justify-start font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />{date ? format(date, "PP") : "Pick date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>Time slot</Label>
              <Select value={time} onValueChange={setTime}>
                <SelectTrigger data-testid="ship-time"><SelectValue placeholder="Pick a time" /></SelectTrigger>
                <SelectContent>{TIME_SLOTS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2"><Label>Notes (optional)</Label><Textarea data-testid="ship-notes" placeholder="Anything we should know? (fragile, needs packing help, etc.)" value={form.notes} onChange={set("notes")} /></div>
          <p className="text-xs text-muted-foreground">Hours: Mon–Fri 9 AM–6 PM, Sat–Sun 10 AM–5 PM. We'll confirm your appointment by phone or email.</p>
          <Button type="submit" disabled={loading} data-testid="ship-submit" className="w-full rounded-full sm:w-auto sm:px-10">
            {loading ? "Reserving..." : "Reserve Drop-Off"}
          </Button>
        </form>
      </section>
    </div>
  );
}
